import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import {
  FiCreditCard, FiCheckCircle, FiXCircle,
  FiUsers, FiTrendingUp, FiTrendingDown, FiEdit, FiEye, FiX, FiSave,
  FiCalendar, FiDollarSign, FiRefreshCw, FiStar, FiPlus
} from 'react-icons/fi';

const EMPTY_PLAN_FORM = {
  planId: '',
  plan_id: '',
  name: '',
  description: '',
  price: 0,
  currency: 'TND',
  billingCycle: 'monthly',
  billing_cycle: 'monthly',
  features: '',
  isActive: true,
  is_active: true,
};

// Composant Metric Card amélioré
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'yellow', trend }) => {
  const getColorClasses = () => {
    const colors = {
      yellow: 'border-yellow-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      green: 'border-green-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      red: 'border-red-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      blue: 'border-blue-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      purple: 'border-purple-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
    };
    return colors[color] || colors.yellow;
  };

  const getIconColor = () => {
    const colors = {
      yellow: 'text-yellow-500',
      green: 'text-green-500',
      red: 'text-red-500',
      blue: 'text-blue-500',
      purple: 'text-purple-500',
    };
    return colors[color] || colors.yellow;
  };

  return (
    <div className={`rounded-xl border ${getColorClasses()} p-6 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-gray-700/50 ${getIconColor()}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? <FiTrendingUp className="w-4 h-4 mr-1" /> : <FiTrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-sm text-gray-400 font-medium">{title}</div>
        <div className="text-3xl font-bold text-white">{value}</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
    </div>
  );
};

// Composant Plan Card amélioré
const PlanCard = ({ plan, onEdit }) => {
  const isPopular = plan.name?.toLowerCase().includes('pro') || plan.name?.toLowerCase().includes('premium');
  
  return (
    <div className={`relative bg-gradient-to-br from-gray-800 to-gray-900 border rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
      isPopular 
        ? 'border-yellow-500/50 shadow-yellow-500/20' 
        : 'border-gray-700 hover:border-yellow-600/50'
    }`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-yellow-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
            POPULAIRE
          </span>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
          <span className={`px-3 py-1 text-xs rounded-full font-medium ${
            plan.isActive 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {plan.isActive ? 'Actif' : 'Inactif'}
          </span>
        </div>
        
        <div className="mb-4">
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-white">{plan.price || 0}</span>
            <span className="text-gray-400 text-lg ml-2">{plan.currency || 'TND'}</span>
            <span className="text-gray-500 text-sm ml-2">
              /{plan.billingCycle === 'yearly' ? 'an' : 'mois'}
            </span>
          </div>
        </div>
        
        {plan.description && (
          <p className="text-gray-400 text-sm mb-6">{plan.description}</p>
        )}
      </div>
      
      <div className="space-y-3 mb-6">
        {plan.features && Array.isArray(plan.features) && plan.features.length > 0 ? (
          plan.features.slice(0, 5).map((feature, index) => (
            <div key={index} className="flex items-start">
              <FiCheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-300">
                {typeof feature === 'string' ? feature : feature.name || JSON.stringify(feature)}
              </span>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 italic">Aucune fonctionnalité spécifiée</p>
        )}
        {plan.features && Array.isArray(plan.features) && plan.features.length > 5 && (
          <p className="text-xs text-gray-500 italic">
            +{plan.features.length - 5} autres fonctionnalités
          </p>
        )}
      </div>
      
      <button 
        onClick={() => onEdit(plan)}
        className="w-full px-4 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center shadow-lg hover:shadow-xl"
      >
        <FiEdit className="w-5 h-5 mr-2" />
        Modifier le plan
      </button>
    </div>
  );
};

function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planModalMode, setPlanModalMode] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editPlanForm, setEditPlanForm] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const applyPlansSocketPayload = useCallback((payload) => {
    if (!payload) return;
    if (Array.isArray(payload.adminPlans) && payload.adminPlans.length > 0) {
      setPlans(payload.adminPlans);
    }
    if (payload.stats) {
      setStats(payload.stats);
    }
  }, []);

  useEffect(() => {
    const onPlansUpdated = (payload) => {
      if (payload?.adminPlans?.length) {
        applyPlansSocketPayload(payload);
      } else {
        loadData();
      }
    };
    socketService.connect().catch(() => {});
    const unsub = socketService.on('subscription_plans_updated', onPlansUpdated);
    return () => {
      unsub?.();
    };
  }, [applyPlansSocketPayload]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const plansResponse = await api.getAllSubscriptionPlans();
      if (plansResponse && plansResponse.success) {
        setPlans(plansResponse.data || []);
      }
      
      const subscriptionsResponse = await api.getAllUserSubscriptions();
      if (subscriptionsResponse && subscriptionsResponse.success) {
        setSubscriptions(subscriptionsResponse.data || []);
      }
      
      const statsResponse = await api.getSubscriptionStats();
      if (statsResponse && statsResponse.success) {
        setStats(statsResponse.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    }
  };

  const closePlanModal = () => {
    setPlanModalMode(null);
    setSelectedPlan(null);
    setEditPlanForm(null);
  };

  const handleCreatePlan = () => {
    setPlanModalMode('create');
    setSelectedPlan(null);
    setEditPlanForm({ ...EMPTY_PLAN_FORM });
  };

  const handleEditPlan = (plan) => {
    setPlanModalMode('edit');
    setEditPlanForm({
      ...plan,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
    });
    setSelectedPlan(plan);
  };

  const handleSavePlan = async () => {
    try {
      if (!editPlanForm || !planModalMode) return;

      setSavingPlan(true);

      const featuresArray = String(editPlanForm.features || '')
        .split('\n')
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const planData = {
        name: editPlanForm.name,
        description: editPlanForm.description,
        price: parseFloat(editPlanForm.price),
        currency: editPlanForm.currency,
        billing_cycle: editPlanForm.billingCycle || editPlanForm.billing_cycle,
        features: featuresArray,
        is_active:
          editPlanForm.isActive !== undefined
            ? editPlanForm.isActive
            : editPlanForm.is_active,
      };

      let response;
      if (planModalMode === 'create') {
        const planId = String(
          editPlanForm.planId || editPlanForm.plan_id || ''
        ).trim();
        if (!planId) {
          alert('Veuillez saisir un identifiant pour le plan (ex: business)');
          return;
        }
        response = await api.createSubscriptionPlan({
          plan_id: planId,
          ...planData,
        });
      } else {
        if (!selectedPlan) return;
        response = await api.updateSubscriptionPlan(
          selectedPlan.planId || selectedPlan.id,
          planData
        );
      }

      if (response && response.success) {
        alert(
          planModalMode === 'create'
            ? 'Plan créé avec succès'
            : 'Plan mis à jour avec succès'
        );
        loadData();
        closePlanModal();
      } else {
        throw new Error(response?.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du plan:', error);
      const msg = error?.message || 'Une erreur est survenue';
      alert(
        planModalMode === 'create'
          ? `Impossible de créer le plan : ${msg}`
          : `Impossible de mettre à jour le plan : ${msg}`
      );
    } finally {
      setSavingPlan(false);
    }
  };

  const handleViewSubscriptionDetails = async (subscriptionId) => {
    try {
      setLoadingDetails(true);
      const response = await api.getUserSubscriptionDetails(subscriptionId);
      if (response && response.success) {
        setSelectedSubscription(response.data);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      alert('Erreur lors du chargement des détails: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoadingDetails(false);
    }
  };

  // Filtrer les abonnements
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = !searchTerm || 
      (sub.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       sub.user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       sub.plan?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
            <FiCreditCard className="w-8 h-8 mr-3 text-yellow-500" />
            Gestion des abonnements
          </h1>
          <p className="text-gray-400">Gérez les plans d'abonnement et les abonnements utilisateurs</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-all flex items-center shadow-lg hover:shadow-xl disabled:opacity-50"
        >
          <FiRefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard
            title="Total abonnements"
            value={stats.total || 0}
            icon={FiUsers}
            color="yellow"
          />
          <MetricCard
            title="Abonnements actifs"
            value={stats.active || 0}
            subtitle={`${stats.expired || 0} expirés`}
            icon={FiCheckCircle}
            color="green"
          />
          <MetricCard
            title="Revenus MRR"
            value={formatCurrency(stats.revenue || 0)}
            subtitle={`${formatCurrency((stats.revenue || 0) * 12)} ARR`}
            icon={FiTrendingUp}
            color="blue"
          />
          <MetricCard
            title="Revenus ARR"
            value={formatCurrency((stats.revenue || 0) * 12)}
            icon={FiDollarSign}
            color="purple"
          />
        </div>
      )}

      {/* Plans disponibles */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <FiStar className="w-6 h-6 mr-3 text-yellow-500" />
            Plans d'abonnement disponibles
          </h2>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full">
              {plans.length} plan{plans.length > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={handleCreatePlan}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center shadow-lg hover:shadow-xl"
            >
              <FiPlus className="w-5 h-5 mr-2" />
              Créer un plan
            </button>
          </div>
        </div>
        
        {loading && plans.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement des plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700/50">
            <FiCreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-300 text-lg font-medium mb-2">Aucun plan d'abonnement</p>
            <p className="text-gray-500 text-sm mb-6">Créez votre premier plan d'abonnement pour commencer</p>
            <button
              type="button"
              onClick={handleCreatePlan}
              className="px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg inline-flex items-center"
            >
              <FiPlus className="w-5 h-5 mr-2" />
              Créer un plan
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <PlanCard key={plan.id || plan.planId} plan={plan} onEdit={handleEditPlan} />
            ))}
          </div>
        )}
      </div>

      {/* Liste des abonnements */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <FiUsers className="w-6 h-6 mr-3 text-blue-500" />
            Abonnements utilisateurs
          </h2>
          <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full border border-blue-500/30">
            {filteredSubscriptions.length} abonnement{filteredSubscriptions.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par utilisateur ou plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            />
            <FiUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="expired">Expirés</option>
            <option value="cancelled">Annulés</option>
          </select>
        </div>

        {loading && subscriptions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement...</p>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700/50">
            <FiCreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-300 text-lg font-medium mb-2">
              {searchTerm || statusFilter !== 'all' ? 'Aucun résultat' : 'Aucun abonnement'}
            </p>
            <p className="text-gray-500 text-sm">
              {searchTerm || statusFilter !== 'all' 
                ? 'Essayez de modifier vos critères de recherche' 
                : 'Aucun abonnement actif pour le moment'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Utilisateur</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-700/20 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold">
                          {(subscription.user?.fullName || subscription.user?.email || 'U')[0].toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {subscription.user?.fullName || subscription.user?.email || 'Utilisateur inconnu'}
                          </div>
                          <div className="text-sm text-gray-400">{subscription.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{subscription.plan?.name || subscription.plan?.id}</div>
                      <div className="text-xs text-gray-400">
                        {subscription.plan?.price || 0} {subscription.plan?.currency || 'TND'} / {subscription.plan?.billingCycle === 'yearly' ? 'an' : 'mois'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        subscription.status === 'active' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : subscription.status === 'expired'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {subscription.status === 'active' && <FiCheckCircle className="w-3 h-3 mr-1" />}
                        {subscription.status === 'expired' && <FiXCircle className="w-3 h-3 mr-1" />}
                        {subscription.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        <div className="flex items-center text-white">
                          <FiCalendar className="w-4 h-4 mr-1 text-gray-400" />
                          {subscription.startDate 
                            ? new Date(subscription.startDate).toLocaleDateString('fr-FR')
                            : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Jusqu'au: {subscription.endDate 
                            ? new Date(subscription.endDate).toLocaleDateString('fr-FR')
                            : 'Illimité'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleViewSubscriptionDetails(subscription.id)}
                        className="text-yellow-400 hover:text-yellow-300 flex items-center transition-colors"
                      >
                        <FiEye className="w-4 h-4 mr-1" />
                        Détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal création / modification de plan */}
      {planModalMode && editPlanForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                {planModalMode === 'create' ? (
                  <>
                    <FiPlus className="w-6 h-6 mr-3 text-yellow-500" />
                    Créer un plan d&apos;abonnement
                  </>
                ) : (
                  <>
                    <FiEdit className="w-6 h-6 mr-3 text-yellow-500" />
                    Modifier le plan {selectedPlan?.name}
                  </>
                )}
              </h2>
              <button
                type="button"
                onClick={closePlanModal}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {planModalMode === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Identifiant du plan <span className="text-gray-500">(ex: business)</span>
                  </label>
                  <input
                    type="text"
                    value={editPlanForm.planId || editPlanForm.plan_id || ''}
                    onChange={(e) =>
                      setEditPlanForm({
                        ...editPlanForm,
                        planId: e.target.value,
                        plan_id: e.target.value,
                      })
                    }
                    placeholder="business"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors font-mono text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                <input
                  type="text"
                  value={editPlanForm.name || ''}
                  onChange={(e) => setEditPlanForm({...editPlanForm, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={editPlanForm.description || ''}
                  onChange={(e) => setEditPlanForm({...editPlanForm, description: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Prix</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editPlanForm.price || 0}
                    onChange={(e) => setEditPlanForm({...editPlanForm, price: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Devise</label>
                  <select
                    value={editPlanForm.currency || 'TND'}
                    onChange={(e) => setEditPlanForm({...editPlanForm, currency: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  >
                    <option value="TND">TND</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Cycle de facturation</label>
                <select
                  value={editPlanForm.billingCycle || editPlanForm.billing_cycle || 'monthly'}
                  onChange={(e) => setEditPlanForm({...editPlanForm, billingCycle: e.target.value, billing_cycle: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                >
                  <option value="monthly">Mensuel</option>
                  <option value="yearly">Annuel</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fonctionnalités (une par ligne)
                </label>
                <textarea
                  value={editPlanForm.features || ''}
                  onChange={(e) => setEditPlanForm({...editPlanForm, features: e.target.value})}
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 font-mono text-sm transition-colors"
                  placeholder="Fonctionnalité 1&#10;Fonctionnalité 2&#10;..."
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editPlanForm.isActive !== undefined ? editPlanForm.isActive : editPlanForm.is_active}
                  onChange={(e) => setEditPlanForm({
                    ...editPlanForm, 
                    isActive: e.target.checked,
                    is_active: e.target.checked
                  })}
                  className="mr-3 w-5 h-5 text-yellow-600 bg-gray-900 border-gray-700 rounded focus:ring-yellow-500"
                />
                <label className="text-sm text-gray-300">Plan actif</label>
              </div>
              
              <div className="flex space-x-4 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={handleSavePlan}
                  disabled={savingPlan}
                  className="flex-1 px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center shadow-lg hover:shadow-xl disabled:opacity-50"
                >
                  <FiSave className="w-5 h-5 mr-2" />
                  {savingPlan
                    ? 'Enregistrement...'
                    : planModalMode === 'create'
                      ? 'Créer le plan'
                      : 'Enregistrer'}
                </button>
                <button
                  type="button"
                  onClick={closePlanModal}
                  disabled={savingPlan}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails d'abonnement */}
      {selectedSubscription && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FiEye className="w-6 h-6 mr-3 text-blue-500" />
                Détails de l'abonnement
              </h2>
              <button
                onClick={() => setSelectedSubscription(null)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            {loadingDetails ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
                <p className="text-gray-400 mt-4">Chargement des détails...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Informations utilisateur */}
                <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <FiUsers className="w-5 h-5 mr-2 text-blue-400" />
                    Informations utilisateur
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 mb-1">Nom complet</p>
                      <p className="text-white font-semibold">{selectedSubscription.user?.fullName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Email</p>
                      <p className="text-white font-semibold">{selectedSubscription.user?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Téléphone</p>
                      <p className="text-white font-semibold">{selectedSubscription.user?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Membre depuis</p>
                      <p className="text-white font-semibold">
                        {selectedSubscription.user?.createdAt 
                          ? new Date(selectedSubscription.user.createdAt).toLocaleDateString('fr-FR')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Informations plan */}
                <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <FiStar className="w-5 h-5 mr-2 text-yellow-400" />
                    Plan d'abonnement
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 mb-1">Nom du plan</p>
                      <p className="text-white font-semibold text-lg">{selectedSubscription.plan?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Prix</p>
                      <p className="text-white font-semibold text-lg">
                        {selectedSubscription.plan?.price || 0} {selectedSubscription.plan?.currency || 'TND'} / {selectedSubscription.plan?.billingCycle === 'yearly' ? 'an' : 'mois'}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-400 mb-1">Description</p>
                      <p className="text-white">{selectedSubscription.plan?.description || 'N/A'}</p>
                    </div>
                    {selectedSubscription.plan?.features && Array.isArray(selectedSubscription.plan.features) && selectedSubscription.plan.features.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-gray-400 mb-3">Fonctionnalités</p>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedSubscription.plan.features.map((feature, index) => (
                            <div key={index} className="flex items-center text-white">
                              <FiCheckCircle className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                              <span className="text-sm">
                                {typeof feature === 'string' ? feature : feature.name || JSON.stringify(feature)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Informations abonnement */}
                <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <FiCalendar className="w-5 h-5 mr-2 text-green-400" />
                    Détails de l'abonnement
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 mb-1">Statut</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        selectedSubscription.status === 'active' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : selectedSubscription.status === 'expired'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {selectedSubscription.status || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Méthode de paiement</p>
                      <p className="text-white font-semibold">{selectedSubscription.paymentMethod || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Date de début</p>
                      <p className="text-white font-semibold">
                        {selectedSubscription.startDate 
                          ? new Date(selectedSubscription.startDate).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Date de fin</p>
                      <p className="text-white font-semibold">
                        {selectedSubscription.endDate 
                          ? new Date(selectedSubscription.endDate).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'Illimité'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Créé le</p>
                      <p className="text-white font-semibold">
                        {selectedSubscription.createdAt 
                          ? new Date(selectedSubscription.createdAt).toLocaleDateString('fr-FR')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 mb-1">Dernière mise à jour</p>
                      <p className="text-white font-semibold">
                        {selectedSubscription.updatedAt 
                          ? new Date(selectedSubscription.updatedAt).toLocaleDateString('fr-FR')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {selectedSubscription.metadata && Object.keys(selectedSubscription.metadata).length > 0 && (
                  <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-bold text-white mb-4">Métadonnées</h3>
                    <pre className="text-xs text-gray-300 bg-gray-950 p-4 rounded-lg overflow-auto border border-gray-800">
                      {JSON.stringify(selectedSubscription.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSubscriptions;
